import type { Customer } from '@gmn-portal/backend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth';

import { payloadClient } from '.';
import type { FindPayloadApiClientParams } from './types';

// Query keys for better type safety and reusability
export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (params: CustomerQueryParams) => [...customerKeys.lists(), params] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerKeys.details(), id] as const,
} as const;

// Types for better type safety
export type CustomerQueryParams = FindPayloadApiClientParams<'customers', Customer> & {
  disabled?: boolean;
};

export const useCustomers = (params?: CustomerQueryParams) => {
  return useQuery({
    queryKey: customerKeys.list(params ?? {}),
    enabled: !params?.disabled,
    queryFn: async () => {
      const response = await payloadClient.find({
        collection: 'customers',
        joins: {
          users: {
            count: true,
            limit: 0, // We only want the count, not the actual user docs
          },
        },
        ...params,
      });
      return response;
    },
  });
};

export const useCurrentUserCustomer = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['currentUserCustomer'],
    queryFn: async () => {
      if (!user?.customer) return null;

      const customerId = typeof user.customer === 'string' ? user.customer : user.customer.id;

      const response = await payloadClient.findById({
        collection: 'customers',
        id: customerId,
      });
      return response;
    },
    enabled: !!user?.customer,
  });
};

export const useCustomer = (id: string) => {
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: async () => {
      const response = await payloadClient.findById({
        collection: 'customers',
        id,
      });
      return response;
    },
    enabled: !!id,
  });
};

export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Customer> }) => {
      const response = await payloadClient.updateById({
        collection: 'customers',
        id,
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
};

export const useCreateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Customer>) => {
      const response = await payloadClient.create({
        collection: 'customers',
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
};

export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await payloadClient.deleteById({
        collection: 'customers',
        id,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
};
